import { useState, useEffect } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { useSearchParams } from 'react-router-dom';
import { Search, Filter, MapPin, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { DoctorCard } from '../components/common/DoctorCard';
import { LogoLoader } from '../components/common/LogoLoader';
import { doctorsAPI, locationsAPI, specialitiesAPI } from '../lib/api';

export const DoctorsPage = () => {
  const seo = usePageSeo();
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    location: searchParams.get('location') || '',
    speciality: searchParams.get('speciality') || '',
    verified: searchParams.get('verified') === 'true',
  });

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [locationsRes, specialitiesRes] = await Promise.all([
          locationsAPI.getAll(),
          specialitiesAPI.getAll(),
        ]);
        setLocations(locationsRes.data);
        setSpecialities(specialitiesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      setLoading(true);
      try {
        const params = {
          doctor_status: 'approved',
          ...(filters.search && { search: filters.search }),
          ...(filters.location && { location_id: filters.location }),
          ...(filters.speciality && { speciality: filters.speciality }),
          ...(filters.verified && { is_verified: true }),
        };
        const response = await doctorsAPI.getAll(params);
        setDoctors(response.data);
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDoctors();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    const actualValue = value === 'all' ? '' : value;
    setFilters(prev => ({ ...prev, [key]: actualValue }));
    if (actualValue) {
      searchParams.set(key, actualValue);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      location: '',
      speciality: '',
      verified: false,
    });
    setSearchParams({});
  };

  const activeFiltersCount = [filters.location, filters.speciality, filters.verified].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-alabaster" data-testid="doctors-page">
      <Seo override={seo} title="Find Verified Dermatologists Near You | DrPortals" description="Browse verified dermatologists, skin specialists and hair experts. Filter by city and speciality, and connect instantly on WhatsApp." />
      {/* Header */}
      <div className="bg-navy-800 text-white py-12 md:py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            {seo.h1 || 'Find Dermatologists'}
          </h1>
          <p className="text-gray-300 max-w-xl">
            Browse our network of verified dermatologists and skin specialists
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Search & Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-8" data-testid="search-filters">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name or clinic..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 h-11 rounded-lg"
                data-testid="search-input"
              />
            </div>

            {/* Location Filter */}
            <Select value={filters.location || 'all'} onValueChange={(val) => handleFilterChange('location', val)}>
              <SelectTrigger className="w-full md:w-48 h-11" data-testid="location-filter">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <SelectValue placeholder="All Cities" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id}>
                    {loc.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Mobile Filter Toggle */}
            <Button
              variant="outline"
              className="md:hidden"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="ml-2 bg-navy-800 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </Button>
          </div>

          {/* Extended Filters */}
          <div className={`${showFilters ? 'block' : 'hidden'} md:flex flex-col md:flex-row gap-4 mt-4 pt-4 border-t border-gray-100`}>
            {/* Speciality Filter */}
            <Select value={filters.speciality || 'all'} onValueChange={(val) => handleFilterChange('speciality', val)}>
              <SelectTrigger className="w-full md:w-56 h-11" data-testid="speciality-filter">
                <SelectValue placeholder="All Specialities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Specialities</SelectItem>
                {specialities.map((spec) => (
                  <SelectItem key={spec.id} value={spec.id}>
                    {spec.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Verified Only */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="verified"
                checked={filters.verified}
                onCheckedChange={(checked) => handleFilterChange('verified', checked)}
                data-testid="verified-filter"
              />
              <label htmlFor="verified" className="text-sm text-gray-700 cursor-pointer">
                Verified Only
              </label>
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-gray-500"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            {loading ? 'Loading...' : `${doctors.length} doctors found`}
          </p>
        </div>

        {/* Doctors Grid */}
        {loading ? (
          <LogoLoader />
        ) : doctors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="doctors-grid">
            {doctors.map((doctor) => (
              <DoctorCard key={doctor.id} doctor={doctor} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16" data-testid="no-doctors">
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-gray-700 mb-2">
              No doctors found
            </h3>
            <p className="text-gray-500 mb-4">
              Try adjusting your filters or search criteria
            </p>
            <Button variant="outline" onClick={clearFilters}>
              Clear All Filters
            </Button>
          </div>
        )}
      </div>
      <PageSeoContent seo={seo} />
    </div>
  );
};
